import shopifyIcon from "@assets/images/shopify-brand.svg";
import { Icon, Typography } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import CardTitle from "@saleor/components/CardTitle";
import FormSpacer from "@saleor/components/FormSpacer";
import MultiAutocompleteSelectField, {
  MultiAutocompleteChoiceType
} from "@saleor/components/MultiAutocompleteSelectField";
import RichTextEditor from "@saleor/components/RichTextEditor";
import { ProductErrorFragment } from "@saleor/fragments/types/ProductErrorFragment";
import { ChangeEvent, FormChange } from "@saleor/hooks/useForm";
import { commonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { PageTypeEnum } from "@saleor/types/globalTypes";
import { getFormErrors, getProductErrorMessage } from "@saleor/utils/errors";
import getDescription from "@saleor/utils/forms/getDescription";
import { RawDraftContentState } from "draft-js";
import React from "react";
import { useIntl } from "react-intl";

import { CollectionDetails_collection } from "../../types/CollectionDetails";

const useStyles = makeStyles(
  {
    shopifyLogoWrapper: {
      "& .shopifyLogo": {
        filter:
          "invert(13%) sepia(26%) saturate(7289%) hue-rotate(210deg) brightness(93%) contrast(106%)"
      },
      fontSize: "1rem"
    },
    smFulfilledIcon: {
      alignItems: "center",
      color: "#002972",
      display: "flex",
      fontWeight: 600,
      justifyContent: "space-evenly",
      width: "8rem"
    }
  },
  { name: "CollectionDetails" }
);

export interface CollectionDetailsProps {
  collection?: CollectionDetails_collection;
  data: {
    description: RawDraftContentState;
    name: string;
    featuredIn: PageTypeEnum[];
  };
  disabled: boolean;
  errors: ProductErrorFragment[];
  onChange: FormChange;
  featuredInDisplayValue: MultiAutocompleteChoiceType[];
  featuredChoices: any[];
  setSelectedFeatured: React.Dispatch<
    React.SetStateAction<MultiAutocompleteChoiceType[]>
  >;
  onFeaturedChange: (event: ChangeEvent) => void;
}

const CollectionDetails: React.FC<CollectionDetailsProps> = ({
  collection,
  disabled,
  data,
  onChange,
  errors,
  featuredInDisplayValue,
  featuredChoices,
  onFeaturedChange
}) => {
  const intl = useIntl();
  const { shopifyLogoWrapper, smFulfilledIcon } = useStyles({});
  const formErrors = getFormErrors(["name", "descriptionJson"], errors);
  return (
    <Card>
      <CardTitle
        title={intl.formatMessage(commonMessages.generalInformations)}
      />
      <CardContent>
        <TextField
          label={intl.formatMessage({
            defaultMessage: "Name",
            description: "collection name"
          })}
          name="name"
          disabled={disabled || collection?.isFromShopify}
          value={data.name}
          onChange={onChange}
          error={!!formErrors.name}
          helperText={getProductErrorMessage(formErrors.name, intl)}
          fullWidth
        />
        <FormSpacer />
        <MultiAutocompleteSelectField
          label={intl.formatMessage({
            defaultMessage: "Featured in"
          })}
          name="featuredIn"
          value={data.featuredIn}
          onChange={onFeaturedChange}
          displayValues={featuredInDisplayValue}
          choices={featuredChoices}
        />
        <FormSpacer />
        <RichTextEditor
          error={!!formErrors.descriptionJson}
          helperText={getProductErrorMessage(formErrors.descriptionJson, intl)}
          initial={maybe(() => getDescription(collection))}
          label={intl.formatMessage(commonMessages.description)}
          name="description"
          disabled={disabled || collection?.isFromShopify}
          onChange={onChange}
        />
        <FormSpacer />
        {collection?.isFromShopify && (
          <Typography className={smFulfilledIcon}>
            <Icon className={shopifyLogoWrapper}>
              <img
                className="shopifyLogo"
                src={shopifyIcon}
                alt="shopify-icon"
              />
            </Icon>
            SM Fulfilled
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
export default CollectionDetails;
